import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Building2 as Building, ChevronDown, ChevronRight, Plus, Search, Home, Camera, ListChecks } from "lucide-react";
import { SECTIONS, ALL_PAGES, HOME, sectionOf, setStatementWords, type NavSection } from "./nav";
import { Help, Palette, type Command } from "./Palette";
import { useShortcuts, type Binding } from "../lib/useShortcuts";
import { label, isTyping } from "../lib/keys";
import { useMe, lastCompany } from "../companies/useMe";
import { signOutEverywhere } from "../lib/supabase";
import { NotInstalled } from "../pages/NotInstalled";
import { CompanyProvider } from "../masters/reference";
import { leaveGuard } from "../ui/leaveGuard";
import { fromUrl, toUrl } from "./routes";
import { ExpenseClaims } from "../claims/ExpenseClaims";
import { Footer } from "./Footer";
import { UserMenu } from "./UserMenu";
import { UpdateNotice } from "./UpdateNotice";

// records whose list isn't simply the path above them
const PARENT: Record<string, string> = { "vouchers/v": "vouchers/daybook", "vouchers/new": "vouchers/daybook" };

// The frame around every company screen: the menu, the top bar with the command bar, keys. The company is the one
// chosen in this tab (companies/useMe lastCompany); the address says only where you are (shell/routes).
export function Shell() {
  const nav = useNavigate();
  const loc = useLocation();
  const { me, error } = useMe();
  const [palette, setPalette] = useState<string | null>(null);     // open, with what was typed to open it
  const [help, setHelp] = useState(false);
  const companyId = lastCompany.get();
  const company = me?.companies.find((c) => c.id === companyId);
  setStatementWords(!!company?.income_expenditure, company?.purpose === "education");   // before the menu draws: a trust's words, a college's students
  const go = useCallback((path: string) => nav(toUrl(path)), [nav]);
  const switchCompany = useCallback(() => nav("/companies"), [nav]);
  const here = fromUrl(loc.pathname);
  const current = sectionOf(here);
  // the menu shows one section open at a time: the one you are in, or the one you opened
  const [openId, setOpenId] = useState<string | null>(current?.id ?? null);
  useEffect(() => { if (current) setOpenId(current.id); }, [current?.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Esc: up one level — a record to its list, a page to its section, a section to Today
  const back = () => {
    if (palette !== null) return setPalette(null);
    if (help) return setHelp(false);
    if (leaveGuard()) return;
    let rest = here.split("/").filter(Boolean);
    if (!rest.length) return;
    const page = ALL_PAGES.find((p) => p.path === here);
    if (page) return nav(toUrl(page.section.id));
    while (rest.length) {
      rest = rest.slice(0, -1);
      const path = rest.join("/");
      if (PARENT[path]) return nav(toUrl(PARENT[path]));
      if (!path || SECTIONS.some((s) => s.id === path) || ALL_PAGES.some((p) => p.path === path)) return nav(toUrl(path));
    }
    return nav(current ? toUrl(current.id) : "/");
  };

  const open = useCallback((initial = "") => { if (!leaveGuard()) setPalette(initial); }, []);
  const bindings: Binding[] = [
    { keys: "Ctrl+K", run: () => open() },
    { keys: "Escape", run: back },
    { keys: "?", run: () => setHelp(true) },
  ];
  useShortcuts(palette !== null || help ? bindings.filter((b) => b.keys === "Escape") : bindings);

  // typing a letter with nothing focused opens the command bar with it, so a code can be typed straight away
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || palette !== null || help) return;
      if (isTyping(e.target) || (document.activeElement && document.activeElement !== document.body)) return;
      if (!/^[a-z0-9]$/i.test(e.key)) return;
      e.preventDefault();
      open(e.key);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [palette, help, open]);

  const extra: Command[] = useMemo(() => [
    { id: "switch", title: "Change company", code: "HM02", run: switchCompany },
    { id: "help", title: "Keyboard and codes", code: "HM09", run: () => setHelp(true) },
    { id: "account", title: "Your account, plan and two-step sign-in", code: "YA10", run: () => { window.location.href = "/account"; } },
    { id: "signout", title: "Sign out", run: () => { lastCompany.clear(); void signOutEverywhere(); } },
  ], [switchCompany]);

  useEffect(() => { if (company) { try { sessionStorage.setItem("books.lastCompanyName", company.name); } catch { /* ignore */ } } }, [company]);
  if (!companyId) return <Navigate to="/companies" replace />;
  if (error?.notInstalled) return <NotInstalled reason={error.code} />;
  if (me && !company) {
    return <main className="center"><div className="card auth-card"><h1>Choose a company</h1>
      <p className="muted">The company this tab was using isn't one of yours any more.</p>
      <button onClick={() => { lastCompany.clear(); switchCompany(); }}>Choose a company</button></div></main>;
  }
  // someone who may only make expense claims (an employee) sees just their claims
  if (company?.claims_only) {
    return (
      <main className="claims-only">
        <header className="topbar">
          <button className="company" onClick={switchCompany} title="Change company"><Building size={16} strokeWidth={1.8} aria-hidden="true" /><span className="name">{company.name}</span><ChevronDown size={14} aria-hidden="true" /></button>
          <span className="grow" />
          <span className="muted small">{me?.email}</span>
          <button className="link" onClick={() => { lastCompany.clear(); void signOutEverywhere(); }}>Sign out</button>
        </header>
        <ExpenseClaims company={company} bare />
      </main>
    );
  }
  const Icon = HOME.icon;

  return (
    <div className="shell">
      <aside className="rail" aria-label="Menu">
        <Link to="/" className="brand"><img className="mark" src="/mark.svg" alt="" width={28} height={28} /><span>Altekore Books</span></Link>
        <nav>
          <NavLink to="/" end className="rail-item"><Icon size={17} strokeWidth={1.8} aria-hidden="true" /><span className="grow">{HOME.title}</span><span className="code">{HOME.code}</span></NavLink>
          {SECTIONS.map((s) => <RailSection key={s.id} s={s} here={here} current={current?.id === s.id} open={openId === s.id}
                                              onToggle={() => setOpenId(openId === s.id ? null : s.id)} />)}
        </nav>
        <div className="rail-foot"><UserMenu email={me?.email} role={company?.role} via={company?.via} onSwitch={switchCompany} onHelp={() => setHelp(true)} /></div>
      </aside>
      <div className="main">
        <header className="topbar">
          <button className="company" onClick={switchCompany} title="Change company (HM02)">
            <Building size={16} strokeWidth={1.8} aria-hidden="true" /><span className="name">{company?.name ?? "…"}</span><ChevronDown size={14} aria-hidden="true" />
          </button>
          <button className="search" onClick={() => open()}>
            <Search size={16} strokeWidth={2} aria-hidden="true" />
            <span>Type a code (SI01) or search</span><kbd>{label("Ctrl+K")}</kbd>
          </button>
          <button className="primary new-invoice" onClick={() => go("vouchers/new/sales")}><Plus size={16} aria-hidden="true" />New Sales Invoice <span className="code">SI01</span></button>
        </header>
        {company ? <CompanyProvider company={company}><Outlet /></CompanyProvider> : <main className="center muted">Loading…</main>}
        <Footer />
        <UpdateNotice />
      </div>
      <nav className="phone-bar" aria-label="Quick">
        <NavLink to="/" end><Home size={20} aria-hidden="true" /><span>Today</span></NavLink>
        <NavLink to="/sales/invoices/new"><Plus size={20} aria-hidden="true" /><span>Invoice</span></NavLink>
        <NavLink to="/purchases/inbox?snap=1"><Camera size={20} aria-hidden="true" /><span>Snap bill</span></NavLink>
        <NavLink to="/accounting/approvals"><ListChecks size={20} aria-hidden="true" /><span>Approve</span></NavLink>
      </nav>
      {palette !== null && <Palette extra={extra} go={go} initial={palette} onClose={() => setPalette(null)} />}
      {help && <Help onClose={() => setHelp(false)} />}
    </div>
  );
}

// one section of the menu: its name opens and closes the list of its pages (open by itself while you are in it);
// the pages to start something and the lists come first, the section's reports under their own heading
const kindOf = (code: string) => { const n = Number(code.slice(2)); return n < 10 ? "start" : n < 20 ? "list" : "report"; };
function RailSection({ s, here, current, open, onToggle }: { s: NavSection; here: string; current: boolean; open: boolean; onToggle: () => void }) {
  const [more, setMore] = useState(false);
  const I = s.icon!;
  const listFor = (p: NavSection["pages"][number]) => s.pages.find((q) => kindOf(q.code) === "list" && q.code.slice(0, 2) === p.code.slice(0, 2));
  const makerOf = (q: NavSection["pages"][number]) => s.pages.find((p) => kindOf(p.code) === "start" && p.code.slice(0, 2) === q.code.slice(0, 2) && /^New /.test(p.title));
  const pages = s.pages.filter((p) => !p.hidden);
  const main = s.id === "reports" ? pages : pages.filter((p) => kindOf(p.code) !== "report" && !(kindOf(p.code) === "start" && /^New /.test(p.title) && listFor(p)));
  const reports = s.id === "reports" ? [] : pages.filter((p) => kindOf(p.code) === "report");
  const inReports = reports.some((p) => here === p.path || here.startsWith(p.path + "/"));
  const Item = ({ p }: { p: NavSection["pages"][number] }) => {
    const on = here === p.path || here.startsWith(p.path + "/") || (here.split("?")[0] === p.path.split("?")[0]);
    const maker = kindOf(p.code) === "list" ? makerOf(p) : undefined;
    const makingOn = maker && (here === maker.path || here.startsWith(maker.path + "/"));
    return <div className={`rail-sub${on || makingOn ? " active" : ""}`}>
      <Link to={toUrl(p.path)} className="grow" title={p.desc}>{p.title}</Link>
      {maker ? <Link to={toUrl(maker.path)} className="rail-plus" title={`${maker.title} (${maker.code})`} aria-label={maker.title}><Plus size={13} /></Link>
             : <span className="code">{p.code}</span>}</div>;
  };
  return (
    <div className={`rail-section${open ? " open" : ""}`}>
      <div className={`rail-item${current ? " active" : ""}`}>
        <button type="button" className="rail-toggle" onClick={onToggle} aria-expanded={open} aria-label={`${open ? "Close" : "Open"} ${s.title}`}>
          {open ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}</button>
        <Link to={toUrl(s.id)} className="rail-link">
          <I size={17} strokeWidth={1.8} aria-hidden="true" /><span className="grow">{s.title}</span><span className="code">{s.code}</span></Link>
      </div>
      {open && <div className="rail-pages">
        {main.map((p) => <Item key={p.id} p={p} />)}
        {reports.length > 0 && <>
          <button type="button" className="rail-more" onClick={() => setMore(!more)} aria-expanded={more || inReports}>
            {more || inReports ? <ChevronDown size={12} aria-hidden="true" /> : <ChevronRight size={12} aria-hidden="true" />} Reports ({reports.length})</button>
          {(more || inReports) && reports.map((p) => <Item key={p.id} p={p} />)}
        </>}
      </div>}
    </div>
  );
}

export function SectionCard({ id }: { id: string }) {
  const s = SECTIONS.find((x) => x.id === id)!;
  return (
    <div className="card section">
      <h2><Link to={toUrl(`${s.id}`)}>{s.title}</Link></h2>
      <ul>{s.pages.filter((p) => !p.hidden).map((p) => (
        <li key={p.id}><span className="code">{p.code}</span><Link to={toUrl(`${p.path}`)}>{p.title}</Link></li>
      ))}</ul>
    </div>
  );
}
